import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import BookAppointmentModal from "../Pages/BookAppointmentModal";
import AppointmentBookedModal from "./AppointmentBookedModal";
import "./ClinicDetails.css";

const ClinicDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSlotSelectionOpen, setIsSlotSelectionOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [doctorSchedule, setDoctorSchedule] = useState();
  const [appointmentType, setAppointmentType] = useState("initial");
  const [appointmentFee, setAppointmentFee] = useState();
  const [showAppointmentBooked, setShowAppointmentBooked] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [dateAlert, setDateAlert] = useState("");

  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  function getDayName(day, idx) {
    return dayNames[idx] || day;
  }
  function isToday(idx) {
    const today = new Date().getDay();
    return idx === (today === 0 ? 6 : today - 1);
  }

  useEffect(() => {
    const fetchClinicDetails = async () => {
      try {
        const response = await axiosInstance.get(
          `/patient/clinics/clinic-details/?id=${id}`
        );

        if (response.data.status) {
          setClinic(response.data.data[0]);
        } else {
          setError("Failed to fetch clinic details.");
        }
      } catch (err) {
        setError("An error occurred while fetching clinic details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinicDetails();
  }, [id]);

  useEffect(() => {
    if (!doctorSchedule) return;
    if (appointmentType === "initial") {
      setAppointmentFee(doctorSchedule.initial_appointment_fee);
    } else {
      setAppointmentFee(doctorSchedule.follow_up_appointment_fee);
    }
  }, [appointmentType, doctorSchedule]);

  const handlePractitionerClick = async (practitioner) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      navigate(`/login?redirectClinic=${id}`);
      return;
    }

    setSelectedPractitioner(practitioner);
    setIsSlotSelectionOpen(true);
    setSelectedSlot(null);
    setSelectedDate("");

    try {
      const scheduleResponse = await axiosInstance.get(
        `/patient/doctor/doctor-schedule/?id=${practitioner.Health_Professional_Id}`
      );

      if (scheduleResponse.data.status) {
        setDoctorSchedule(scheduleResponse.data.data || {});
      } else {
        toast.error("Failed to fetch doctor schedule.");
      }
    } catch (error) {
      console.error("Error fetching doctor schedule:", error);
      toast.error("Error loading doctor's schedule.");
    }
  };


  
  const handleDateChange = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);

    try {
      const slotsResponse = await axiosInstance.get(
        `/patient/doctor/doctor-slots/?doctorid=${selectedPractitioner.Health_Professional_Id}&date=${date}`
      );

      if (slotsResponse.data.status) {
        const { starttiming } = slotsResponse.data;
        const { endtiming } = slotsResponse.data;
        const availableSlots = (starttiming || []).map((start, index) => ({
          start,
          end: (endtiming || [])[index] || "",
        }));
        setAvailableSlots(availableSlots);
      } else {
        toast.error("No available slots found for the selected date.");
      }
    } catch (error) {
      console.error("Error fetching available slots:", error);
      toast.error("Failed to load available slots.");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPractitioner(null);
  };

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
  };

  const handleAppointmentBooked = (appointmentId) => {
    setAppointmentId(appointmentId);
    setShowAppointmentBooked(true);
    setIsModalOpen(false);
  };

  const handleProceedToBooking = () => {
    if (!selectedDate) {
      setDateAlert("Please select a date before proceeding to booking.");
      return;
    }
    if (selectedSlot && selectedDate && selectedPractitioner) {
      setIsSlotSelectionOpen(false);
      setIsModalOpen(true);
      setDateAlert("");
    } else {
      setDateAlert("Please select a date and slot before proceeding.");
    }
  };

  if (loading) return <p>Loading clinic details...</p>;
  if (error) return <p className="error">{error}</p>;
  if (!clinic) return <p>Clinic not found</p>;

  return (
    <div className="clinic-details">
      <div className="clinic-details-top">
        <div className="clinic-details-left">
          <div className="clinic-header-row">
            <img
              className="clinic-profile-image"
              src={clinic.Hospital_Image || "../dummy-profile.png"}
              alt="Clinic"
            />
            <h1>{clinic.Hospital_Name}</h1>
          </div>
          <div className="clinic-text-details">
            <div className="clinic-info-text">
              <p>
                <strong>Address</strong>
                <br />
                {clinic.address || "Not provided"}
              </p>
              {clinic.phone_number &&
                Object.keys(clinic.phone_number).length > 0 && (
                  <p>
                    <strong>Phone</strong>
                    <br />
                    {clinic.phone_number.contact || "Not provided"}
                  </p>
                )}
              <p>
                <strong>Specialties</strong>
                <br />
                {clinic.Hospital_Speciality}
              </p>
            </div>
          </div>
        </div>
        <div className="clinic-details-right">
          {clinic.weekday && Object.keys(clinic.weekday || {}).length > 0 && (
            <div className="clinic-opening-hours">
              <h3>Opening Hours</h3>
              <table className="opening-hours-table">
                <tbody>
                  {Object.entries(clinic.weekday || {}).map(
                    ([day, hours], idx) => (
                      <tr key={day}>
                        <td
                          className={`oh-hours${isToday(idx) ? " today" : ""}`}
                        >
                          {hours || "Closed"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <h2>Practitioners</h2>
      <ul className="practitioner-list">
        {clinic.doctors.length > 0 ? (
          clinic.doctors.map((practitioner) => (
            <li
              key={practitioner.Health_Professional_Id}
              className="practitioner-item"
            >
              <div className="practitioner-subitems">
                <img
                  src={practitioner.Health_Professional_Image}
                  alt={practitioner.Full_Name}
                  className="practitioner-item-img"
                />
                <div className="practitioner-item-details">
                  <strong>{practitioner.Full_Name}</strong>
                  <p>Specialty: {practitioner.Doctor_Speciality}</p>
                  <p>Rating: {practitioner.Doctor_rating || "N/A"}</p>
                </div>
              </div>
              <button
                className="book-button"
                onClick={() => handlePractitionerClick(practitioner)}
              >
                Book Now
              </button>
            </li>
          ))
        ) : (
          <p>No practitioners available.</p>
        )}
      </ul>

      {isSlotSelectionOpen && selectedPractitioner && (
        <div className="slot-selection-overlay">
          <div className="slot-selection-modal">
            <h2>Select a Slot with {selectedPractitioner.Full_Name}</h2>

            {doctorSchedule && (
              <div className="doctor-schedule">
                <h3>Doctor's Weekly Availability</h3>
                <ul>
                  {Object.entries(doctorSchedule || {}).map(
                    ([day, isAvailable]) =>
                      day.includes("_Duration") && (
                        <li key={day}>
                          <strong>{day.replace("_Duration", "")}:</strong>{" "}
                          {isAvailable === "true"
                            ? "Available"
                            : "Not Available"}
                        </li>
                      )
                  )}
                </ul>
              </div>
            )}

            {doctorSchedule && (
              <div className="appointment-type-section">
                <div>
                  <label htmlFor="appointment-type-select">
                    Appointment Type
                  </label>
                  <select
                    id="appointment-type-select"
                    value={appointmentType}
                    onChange={(e) => setAppointmentType(e.target.value)}
                  >
                    <option value="initial">Initial</option>
                    <option value="follow_up">Follow Up</option>
                  </select>
                </div>
                <span>
                  Fee: <strong>{appointmentFee}</strong>{" "}
                  {doctorSchedule?.Currency}
                </span>
              </div>
            )}

            <div className="slot-selection">
              <h3>Select a Date</h3>
              <input
                className="pick-date-for-slot"
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  handleDateChange(e.target.value);
                  setDateAlert("");
                }}
              />

              {dateAlert && (
                <div style={{ color: "red", margin: "10px 0" }}>
                  {dateAlert}
                </div>
              )}

              {selectedDate && availableSlots.length > 0 ? (
                <div className="slot-list">
                  <h3>Available Slots</h3>
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      className={`slot-btn ${
                        selectedSlot === slot.start ? "selected" : ""
                      }`}
                      onClick={() => setSelectedSlot(slot.start)}
                    >
                      {slot.start} - {slot.end}
                    </button>
                  ))}
                </div>
              ) : selectedDate ? (
                <p>No slots available for the selected date.</p>
              ) : null}
            </div>

            <button
              className="proceed-button"
              disabled={!selectedSlot}
              onClick={handleProceedToBooking}
            >
              Proceed to Booking Form
            </button>

            <button
              className="close-button"
              onClick={() => setIsSlotSelectionOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {isModalOpen && selectedPractitioner && (
        <BookAppointmentModal
          practitioner={selectedPractitioner}
          selectedDate={selectedDate}
          selectedSlot={selectedSlot}
          onClose={handleCloseModal}
          appointmentType={appointmentType}
          appointmentFee={appointmentFee}
          onAppointmentBooked={handleAppointmentBooked}
        />
      )}

      {showAppointmentBooked && (
        <AppointmentBookedModal
          appointment_id={appointmentId}
          onClose={() => setShowAppointmentBooked(false)}
        />
      )}
    </div>
  );
};

export default ClinicDetails;
